import { supabase } from '@/lib/supabase';
import { invokeForgeWorker } from '@/lib/forge-worker-client';

/**
 * An accepted recommendation applied to the product: the analyst drafts the
 * change (profile entries, or one objective), the manager edits it, then the
 * page writes it under the manager's own rights.
 */

export interface ProfileChange { entryId: string | null; category: string; title: string; content: string; why: string }
export type ApplyDraft =
  | { area: 'profile'; changes: ProfileChange[] }
  | { area: 'objectives'; title: string; successCriteria: string; dueDate: string | null; why: string };

const record = (value: unknown): Record<string, unknown> => (typeof value === 'object' && value !== null ? value as Record<string, unknown> : {});

export const prepareApply = async (proposalId: string): Promise<ApplyDraft> => {
  const payload = record(await invokeForgeWorker({ action: 'analyst_apply', proposalId }));
  const draft = record(payload.draft);
  if (draft.area === 'profile' && Array.isArray(draft.changes)) return draft as unknown as ApplyDraft;
  if (draft.area === 'objectives' && typeof draft.title === 'string') return draft as unknown as ApplyDraft;
  throw new Error('La préparation n’a pas abouti. Réessayez.');
};

/** The current text of the profile entries a draft rewrites, to show before and after. */
export const loadProfileEntries = async (ids: string[]): Promise<Map<string, { title: string; content: string }>> => {
  if (!ids.length) return new Map();
  const { data, error } = await supabase.from('tenant_context').select('id, title, content').in('id', ids);
  if (error) throw new Error('Le profil n’est pas accessible pour le moment.');
  return new Map((data ?? []).map((row) => [row.id as string, { title: row.title as string, content: row.content as string }]));
};

export const applyProfileChanges = async (changes: ProfileChange[]): Promise<void> => {
  for (const change of changes) {
    const values = { category: change.category, title: change.title.trim(), content: change.content.trim() };
    const { error } = change.entryId
      ? await supabase.from('tenant_context').update(values).eq('id', change.entryId)
      : await supabase.from('tenant_context').insert(values);
    if (error) throw new Error('Le profil n’a pas été mis à jour. Vérifiez vos droits, puis réessayez.');
  }
};

export const createCompanyObjective = async (draft: { title: string; successCriteria: string; dueDate: string | null }): Promise<void> => {
  const { error } = await supabase.from('workspace_objectives').insert({
    scope: 'company', owner_user_id: null,
    title: draft.title.trim(), success_criteria: draft.successCriteria.trim(), due_date: draft.dueDate || null,
  });
  if (error) throw new Error('L’objectif n’a pas été créé. Vérifiez vos droits, puis réessayez.');
};

/** Providers connected by the current person (Connexions), to tick connection steps by themselves. */
export const loadConnectedProviders = async (): Promise<Set<string>> => {
  const { data, error } = await supabase.from('integrations').select('provider').eq('is_active', true);
  // Detection only: without it, connection steps are ticked by hand.
  if (error) return new Set();
  return new Set((data ?? []).map((row) => row.provider as string));
};
